// get the current subscription, and current agent
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v2SubscriptionsApi } from 'api';
import { AuthContext } from './AuthContext';
import { LoadingScreen } from 'components';

export const SubscriptionContext = createContext();

export const subscriptionQueryKeys = {
  plans: ['v2', 'subscription', 'plans'],
  list: ['v2', 'subscription', 'list'],
};

const SUB_STALE_MS = 15 * 60 * 1000;

function sortPlans(availablePlansResponse) {
  const list = Array.isArray(availablePlansResponse) ? [...availablePlansResponse] : [];
  return list.sort((a, b) => {
    if (a.type === 'default_free') return -1;
    if (b.type === 'default_free') return 1;
    return a.max_campaigns - b.max_campaigns;
  });
}

export const SubscriptionProvider = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const enabled = Boolean(auth.token);

  const plansQuery = useQuery({
    queryKey: subscriptionQueryKeys.plans,
    queryFn: () => v2SubscriptionsApi.getPlans(),
    enabled,
    staleTime: SUB_STALE_MS,
    gcTime: 60 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const subsQuery = useQuery({
    queryKey: subscriptionQueryKeys.list,
    queryFn: () => v2SubscriptionsApi.getSubscriptions(),
    enabled,
    staleTime: SUB_STALE_MS,
    gcTime: 60 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const availablePlans = useMemo(() => sortPlans(plansQuery.data), [plansQuery.data]);

  const currentSubscription = useMemo(() => {
    const subscriptionResponse = subsQuery.data;
    if (!Array.isArray(subscriptionResponse)) return null;
    const filtered = subscriptionResponse.filter(
      (subscription) =>
        subscription.status === 'active' || subscription.current_status === 'active_until_next_payment'
    );
    return filtered?.[0] || null;
  }, [subsQuery.data]);

  const currentPlan = useMemo(() => {
    const defaultFreePlan = availablePlans.find((plan) => plan.type === 'default_free');
    if (currentSubscription?.id) {
      return availablePlans.find((plan) => plan.id === currentSubscription.plan_id) || defaultFreePlan;
    }
    return defaultFreePlan || null;
  }, [availablePlans, currentSubscription]);

  const isFreePlan = !currentPlan || currentPlan.type === 'default_free';

  const fetchSubscriptionData = useCallback(async () => {
    if (!auth.token) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.plans }),
      queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.list }),
    ]);
  }, [auth.token, queryClient]);

  const initialLoading = enabled && (plansQuery.isLoading || subsQuery.isLoading);

  const value = {
    currentPlan,
    isFreePlan,
    availablePlans,
    currentSubscription,
    fetchSubscriptionData,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {initialLoading ? <LoadingScreen /> : children}
    </SubscriptionContext.Provider>
  );
};
